import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import BubbleNav from 'Components/bubble-navigation'
import TextArea from './TextArea'
import Controller, { IPublishProps } from 'Components/publisher/controller'
import { getKey } from 'lib/get-key'
import Toast from 'Components/toast-messager'
import { stringify } from 'querystring'
import {
  DELAY_PUBLISH_PICTURE_API,
  DELAY_PUBLISH_VIDEO_API,
  PUBLISH_PICTURE_API,
  PUBLISH_VIDEO_API,
} from 'Constants/community-api'
import useCache from 'Components/hooks/use-cache-store'
import { GlobalStoreContext } from 'Components/store'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import fetch from 'lib/decorated-fetch'
import { aid } from 'Constants/index'

const go_to_detail_icon = require('static/go-to-detail.png')

export interface IPB extends IPublishProps {
  type: string
  content: string
  gid: string
  user_id?: string
}

export type PublishType = 'video' | 'qa' | 'picture'

interface IPublisher {
  onPublish: (p: IPB) => void
  defaultCommunity?: {
    series_id: string
    series_name: string
  }
  defaultTopic?: {
    id: number | string
    name: string
  }
  tabs?: Array<{
    key: PublishType | 'long'
    value: ReactNode
  }>
  defaultTab?: string
}

const handleClick = (e: React.MouseEvent) => {
  e.stopPropagation()
  e.nativeEvent.stopImmediatePropagation()
}

const LongArticleNavItem = () => (
  <a
    href="https://mp.dcdapp.com/ugc/publish/#/post"
    // eslint-disable-next-line react/jsx-no-target-blank
    target="_blank"
    rel="unsafe-url"
    onClick={handleClick}
  >
    <span className="item-with-icon" key={''}>
      长帖
    </span>
    <style jsx>
      {`
        a {
          color: inherit;
          text-decoration: inherit;
        }

        .item-with-icon {
          position: relative;
        }

        .item-with-icon::after {
          content: '';
          background-image: url('${go_to_detail_icon}');
          background-position: center;
          background-size: cover;
          position: absolute;
          right: -16px;
          top: 50%;
          transform: translateY(-50%);
          width: 16px;
          height: 16px;
        }
      `}
    </style>
  </a>
)

export default function Publisher({
  onPublish,
  defaultCommunity,
  defaultTopic,
  tabs = [
    { key: 'picture', value: '图文' },
    { key: 'video', value: '小视频' },
    { key: 'qa', value: '问答' },
    { key: 'long', value: <LongArticleNavItem key={'whatever'} /> },
  ],
  defaultTab,
}: IPublisher) {
  const [activeNow, setActiveNow] = useState(defaultTab || tabs[0]?.key)
  useEffect(() => {
    setActiveNow(defaultTab || tabs[0]?.key)
  }, [defaultTab, tabs[0]?.key])
  const [content, setContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [buttonDisabled, setButtonDisabled] = useState(true)
  const { getStorage, setStorage, clearStorage } = useCache()
  const { state: globalState } = useContext(GlobalStoreContext)
  const [publishing, setPublishing] = useState(false)
  useEffect(() => {
    setButtonDisabled(content === '')
  }, [content])

  useEffect(() => {
    setContent(getStorage(getKey('TEXT', activeNow)) || '')
  }, [activeNow])

  const clearLastStorage = useCallback(() => {
    ;[
      'TEXT',
      'VIDEO',
      'VIDEO_INFO',
      'IMAGES',
      'COMMUNITY_STATES',
      'TOPIC',
      'PUBLISH_DATE',
    ].forEach((type) => {
      clearStorage(getKey(type, activeNow))
    })
  }, [activeNow])

  const handleActiveChange = useCallback((index: string) => {
    setActiveNow(index)
  }, [])

  const handleTextAreaChange = useCallback(
    (value: string) => {
      const slicedValue = value.slice(0, 2000)
      try {
        setStorage(getKey('TEXT', activeNow), slicedValue, setSaving)
      } catch (error) {
        console.error(error)
      }

      setContent(slicedValue)
    },
    [activeNow, setSaving]
  )

  const [qaSwitch, setQASwitch] = useState('')
  const qaSwitches = useMemo(() => {
    if (qaSwitch === '') {
      return ['image', 'video']
    }
    return [qaSwitch]
  }, [qaSwitch])
  const getSwitches = useMemo(() => {
    switch (activeNow) {
      case 'picture':
        return ['image', 'community', 'topic', 'publish_date']
      case 'video':
        return ['video', 'community', 'topic', 'publish_date']
      case 'qa':
        return [...qaSwitches, 'community', 'topic', 'publish_date']
      default:
        throw new Error()
    }
  }, [activeNow, qaSwitches])

  const getPlaceHolder = useMemo(() => {
    switch (activeNow) {
      case 'picture':
        return '分享汽车生活 （2000字以内）'
      case 'video':
        return '请填写5-50字的视频描述'
      case 'qa':
        return '有问必答，快速解决汽车问题'
      case 'long':
        return ''
      default:
        throw new Error('unknown active index')
    }
  }, [activeNow])

  const group_type = useMemo(() => {
    switch (activeNow) {
      case 'picture':
        return '图文'
      case 'video':
        return '小视频'
      case 'qa':
        return '问答'
      default:
        return
    }
  }, [activeNow])

  /* eslint-disable complexity */
  const handlePublish = async (props: IPublishProps) => {
    setPublishing(true)
    if (!globalState?.userData?.user_id) {
      Toast.show({
        text: '发布前需要先登录',
      })
      sendTeaCommEvent('click_article_publish_window_submit', {
        submit_status: 'failed',
        group_type,
      })
      setPublishing(false)
      return
    }
    const { imgs, video, community, topic, time, async_to_toutiao, isTimer } =
      props
    const { video_id, videoInfo } = video || {}
    const { id: act_id = 0, name: act_name } = topic || {}
    const { motor_id, series_id, communityName } = community

    if (
      (activeNow === 'video' || activeNow === 'qa') &&
      video &&
      content.length < 5
    ) {
      Toast.show({
        text: '视频文案至少5字',
      })
      sendTeaCommEvent('click_article_publish_window_submit', {
        submit_status: 'failed',
        group_type,
      })
      setPublishing(false)
      return
    }

    if (activeNow === 'video' && !video_id) {
      Toast.show({
        text: '请确认上传视频',
      })
      sendTeaCommEvent('click_article_publish_window_submit', {
        submit_status: 'failed',
        group_type,
      })
      setPublishing(false)
      return
    }
    let params: any
    let json
    try {
      if (activeNow === 'picture' || activeNow === 'qa') {
        params = {
          aid,
          sync_to_toutiao: async_to_toutiao ? '1' : '0', // 是否同步到微头条 1 是 0 否
          content, // 文本内容
          image_urls: imgs.map((e) => e.uri).join(','), // 照片，一个以逗号连接的字符串
          act_id, // 话题id
          source_from: 8,
          source_v2: 10,
        }
        if (series_id || defaultCommunity?.series_id) {
          params.series_id = series_id || defaultCommunity?.series_id
        } else if (motor_id) {
          params.motor_id = motor_id
        }
        if (activeNow === 'qa') {
          params = {
            ...params,
            video_id,
            ...videoInfo,
            question: content,
            motor_type: 3,
          }
        }
        if (isTimer && time) {
          params.task_publish_time = time.unix()
        }

        json = await fetch(
          isTimer ? DELAY_PUBLISH_PICTURE_API : PUBLISH_PICTURE_API,
          {
            method: 'POST',
            body: stringify(params),
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
            },
            credentials: 'include',
          }
        )
      } else if (activeNow === 'video') {
        params = {
          aid,
          video_type: 2,
          thumb_source: 1,
          motor_source: 8,
          source_v2: 10,
          video_id,
          description: content,
          series_id,
          motor_id,
          async_to_toutiao: async_to_toutiao ? '1' : '0',
          act_id,
          ...videoInfo,
        }
        if (isTimer && time) {
          params.task_publish_time = time.unix()
        }

        json = await fetch(
          isTimer ? DELAY_PUBLISH_VIDEO_API : PUBLISH_VIDEO_API,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
            },
            body: stringify(params),
          }
        )
      }
      if (
        json.status !== 'success' &&
        json.status !== 0 &&
        json.err_no !== 0 &&
        !json.data?.id
      ) {
        const errMsg = json.prompts || json.error_show || json.err_tips
        console.error(errMsg)
        Toast.show({
          text: `发布失败 ${errMsg}`,
        })
        sendTeaCommEvent('click_article_publish_window_submit', {
          submit_status: 'failed',
          group_type,
        })
        setPublishing(false)
        return
      }
      clearLastStorage()
      if (isTimer) {
        Toast.show({
          text: '定时发布成功',
        })
      } else {
        Toast.show({
          text: '发布成功',
        })
      }
      onPublish({
        ...props,
        type: activeNow,
        content,
        gid: json.data?.item_id_str || json.data?.gid_str,
      })
      sendTeaCommEvent('click_article_publish_window_submit', {
        submit_status: 'success',
        group_type,
      })
      setPublishing(false)
    } catch (error) {
      console.error(error)
      Toast.show({
        text: '发布失败 网络异常',
      })
      sendTeaCommEvent('click_article_publish_window_submit', {
        submit_status: 'failed',
        group_type,
      })
      setPublishing(false)
    }
  }
  return (
    <div className="publisher">
      <div className="header">
        <BubbleNav
          items={tabs}
          onActivate={handleActiveChange}
          activeIndex={activeNow}
          className="bubble-nav-outer"
        />
        <div className="saving-status">
          {content === '' ? (
            <>
              <span className="saving-status-seconds">3s&nbsp;</span>
              自动保存
            </>
          ) : saving ? (
            '保存中...'
          ) : (
            '已保存'
          )}
        </div>
      </div>
      <TextArea
        onChange={handleTextAreaChange}
        content={content}
        placeholder={getPlaceHolder}
        className={'textarea'}
        max_words={activeNow === 'video' ? 50 : 2000}
      />
      <Controller
        switches={getSwitches}
        setSaving={setSaving}
        activeNow={activeNow}
        className={'controller'}
        onPublish={handlePublish}
        buttonDisabled={buttonDisabled}
        setQASwitch={setQASwitch}
        publishing={publishing}
        defaultCommunity={defaultCommunity}
        defaultTopic={defaultTopic}
      />
      <style jsx>
        {`
          .publisher {
            width: 800px;
            padding: 24px;
            background-color: white;
            position: relative;
          }

          .saving-status {
            position: absolute;
            top: 34px;
            right: 24px;
            font-size: 14px;
            line-height: 20px;
            color: #606370;
          }

          .saving-status-seconds {
            color: #e65800;
          }

          :global(.textarea) {
            margin-top: 22px;
          }

          :global(.textarea .textarea-simulator) {
            height: 236px;
          }

          :global(.controller) {
            margin-top: 8px;
          }
          :global(.MuiPopover-root, .MuiAutocomplete-popper) {
            z-index: 10201 !important;
          }
        `}
      </style>
    </div>
  )
}
